/**
 * Data cores as keys I: deliver for standing (economy plan SY-7, direction §2.6 "Data as keys", D-TBD 12).
 *
 * After M7 S14 copies a generic core's log into the story ledger on pickup (`cargo.ts addComponent`), the core itself
 * can be **sold** (only the Hub buys cores, D2-FC #8; `economy/trade.ts`), **delivered** here, or kept (MC-h5's
 * decode, later). Delivering hands the core to a market station of `salvage.json coreDelivery.faction` for standing
 * with that faction, no credits:
 *
 * - **What:** a generic core only (`story/hooks.ts isGenericCore`): story items stay sealed (M7 S4, T13), and sealed
 *   or contract cargo is never handed over.
 * - **The log stays:** the core leaves the hold, but `career.dataCores` and the story ledger keep its entry, so a
 *   delivered core never logs again and the clue budget (M7 S7) is untouched (a core is never a lead).
 * - **Once per core:** the standing comes with the core leaving the hold, and core ids are unique (BUG-B1 derelict
 *   keys carry the derelict seed, combat-wreck keys a run-wide sequence, and a taken part never respawns), so the
 *   same core can't be delivered twice and the Career needs no delivery record (no save change).
 * - **How much:** `standing × perRegion[region]`, rounded, where `region` is the galaxy region of the system the
 *   delivery is made in (×1 when unknown). A delivery the ±100 clamp would swallow whole is refused (nothing to gain),
 *   so a core is never thrown away.
 * - **Retaliation:** the hand-over counts as a component sale for the M7 S14 station refusals (`marketRefusal`
 *   `sellComponent`), so a station that refuses salvage refuses cores too.
 * - Not gated by `goods.tradeEnabled`: no goods move.
 *
 * Pure: Career in, Career out.
 */
import type { M2Data, StationDef } from '../data/m2Schemas'
import type { RegionId } from '../galaxy/types'
import { isGenericCore } from '../story/hooks'
import { marketRefusal } from '../story/refusals'
import { clampStanding, withoutComponent } from './ops'
import { fail, type CmdResult } from './result'
import { adjustStanding } from './standing'
import type { CargoComponent, Career } from './types'

export type CoreDeliveryConfig = M2Data['salvage']['coreDelivery']

export const CORE_TEXTS = {
  noService: '{station} does not take data cores.',
  notInCargo: 'That data core is not in the hold.',
  notCore: 'Only salvaged data cores can be delivered.',
  sealed: 'Contract and story cargo cannot be handed over.',
  maxed: 'Your standing there cannot rise any further.',
} as const

/** True when `station` takes data cores for standing: a market station of the delivery faction. */
export function takesCoresAt(station: Pick<StationDef, 'faction' | 'services'>, cfg: CoreDeliveryConfig): boolean {
  return station.faction === cfg.faction && station.services.includes('market')
}

/** The standing one delivered core gives in `region` (×1 when the region is unknown). */
export function coreStanding(cfg: CoreDeliveryConfig, region: RegionId | null): number {
  return Math.round(cfg.standing * (region === null ? 1 : cfg.perRegion[region]))
}

/** True for a hold component that can be delivered (a generic core: not story, sealed or contract cargo). */
export function deliverableCore(c: CargoComponent): boolean {
  return isGenericCore(c)
}

/**
 * Delivers the hold data core `coreId` at `station` for standing with the station's faction (no credits). Refuses
 * (Career untouched) at a station that doesn't take cores, under a retaliation refusal, for a component not in the
 * hold, not a core, a story, sealed or contract core, or when the standing is already at the cap.
 */
export function deliverCore(
  career: Career,
  data: Pick<M2Data, 'salvage'>,
  station: StationDef,
  coreId: string,
  region: RegionId | null,
): CmdResult<Career> {
  const cfg = data.salvage.coreDelivery
  if (!takesCoresAt(station, cfg)) {
    return fail('serviceUnavailable', CORE_TEXTS.noService.replace('{station}', station.name), { station: station.id })
  }
  const refused = marketRefusal(career, station, 'sellComponent')
  if (refused !== null) return refused
  const core = career.cargo.components.find((c) => c.id === coreId)
  if (core === undefined) return fail('notInCargo', CORE_TEXTS.notInCargo, { id: coreId })
  if (core.type !== 'dataCore') return fail('notBoughtHere', CORE_TEXTS.notCore, { id: core.id })
  if (!deliverableCore(core)) return fail('notBoughtHere', CORE_TEXTS.sealed, { id: core.id })
  const gain = coreStanding(cfg, region)
  const before = career.standings[cfg.faction]
  if (clampStanding(before + gain) === before) return fail('invalidAmount', CORE_TEXTS.maxed, { faction: cfg.faction })
  return adjustStanding(withoutComponent(career, core.id), cfg.faction, gain)
}
